import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { PageSkeleton } from '@/components/ui/states';
import './index.css';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const LernpfadPage = lazy(() => import('@/pages/LernpfadPage'));
const UnitPage = lazy(() => import('@/pages/UnitPage'));
const TrainingPage = lazy(() => import('@/pages/TrainingPage'));
const QuizSessionPage = lazy(() => import('@/pages/QuizSessionPage'));
const ResultPage = lazy(() => import('@/pages/ResultPage'));
const HistoryPage = lazy(() => import('@/pages/HistoryPage'));
const ReadingListPage = lazy(() => import('@/pages/ReadingListPage'));
const ReadingPage = lazy(() => import('@/pages/ReadingPage'));
const WritingListPage = lazy(() => import('@/pages/WritingListPage'));
const WritingTaskPage = lazy(() => import('@/pages/WritingTaskPage'));
const SubmissionPage = lazy(() => import('@/pages/SubmissionPage'));
const GlossaryPage = lazy(() => import('@/pages/GlossaryPage'));
const MistakesPage = lazy(() => import('@/pages/MistakesPage'));
const ProgressPage = lazy(() => import('@/pages/ProgressPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
  },
});

function Splash() {
  return (
    <div className="grid min-h-screen place-items-center bg-cream">
      <div className="text-center">
        <div className="text-[10px] font-bold tracking-[.32em] text-wine">DEUTSCH</div>
        <div className="mt-1 font-serif text-3xl">Mayras Lernraum.</div>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function RequireOwner({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== 'OWNER') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Suspense fallback={<Splash />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {(
          [
            ['/', <DashboardPage />],
            ['/lernpfad', <LernpfadPage />],
            ['/lernpfad/:code', <UnitPage />],
            ['/training', <TrainingPage />],
            ['/training/sitzung', <QuizSessionPage />],
            ['/ergebnis/:id', <ResultPage />],
            ['/verlauf', <HistoryPage />],
            ['/lesen', <ReadingListPage />],
            ['/lesen/:slug', <ReadingPage />],
            ['/schreiben', <WritingListPage />],
            ['/schreiben/aufgabe/:slug', <WritingTaskPage />],
            ['/schreiben/text/:id', <SubmissionPage />],
            ['/glossar', <GlossaryPage />],
            ['/fehler', <MistakesPage />],
            ['/fortschritt', <ProgressPage />],
            ['/konto', <AccountPage />],
            [
              '/nutzer',
              <RequireOwner>
                <UsersPage />
              </RequireOwner>,
            ],
            ['*', <NotFoundPage />],
          ] as const
        ).map(([path, el]) => (
          <Route key={path} path={path} element={<Suspense fallback={<PageSkeleton />}>{el}</Suspense>} />
        ))}
      </Route>
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <App />
          <Toaster
            position="top-center"
            toastOptions={{
              classNames: {
                toast: '!rounded-2xl !border-line !bg-paper !text-ink !font-sans !shadow-lift',
                description: '!text-ink-soft',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
